import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Sparkles, Lock } from 'lucide-react';
import FunnelCanvas from './FunnelCanvas';

export default function HeroSection({ isUnlocked, onScrollToGate }) {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Animated Background */}
      <FunnelCanvas />
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-transparent to-[#030303] z-10 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/50 via-transparent to-[#030303]/50 z-10 pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-20 container-custom text-center px-4">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs uppercase tracking-widest text-cyan-400">
            <Sparkles className="w-3 h-3" />
            Interactive Funnel Guide
          </span>
        </motion.div>
        
        {/* Main Headline */}
        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6"
        >
          <span className="gradient-text">From Attention</span>
          <br />
          <span className="text-white">to Revenue</span>
        </motion.h1>
        
        {/* Subheadline */}
        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed"
        >
          Discover exactly how a complete marketing funnel transforms traffic into predictable growth.
          <br className="hidden md:block" />
          An interactive guide to automated systems that work while you sleep.
        </motion.p>
        
        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          {isUnlocked ? (
            <>
              <motion.button
                data-testid="hero-explore-button"
                onClick={() => scrollToSection('#problem')}
                className="btn-primary flex items-center gap-2 text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Play className="w-5 h-5" />
                <span>Explore the Funnel</span>
              </motion.button>
              
              <motion.button
                data-testid="hero-book-button"
                onClick={() => scrollToSection('#contact')}
                className="btn-secondary flex items-center gap-2 text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Book a Strategy Call</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </>
          ) : (
            <>
              <motion.button
                data-testid="hero-unlock-button"
                onClick={() => {
                  // On mobile, scroll directly to form
                  const formElement = document.querySelector('[data-testid="gate-first-name"]');
                  if (formElement && window.innerWidth < 1024) {
                    formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else {
                    onScrollToGate();
                  }
                }}
                className="btn-primary flex items-center gap-2 text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Lock className="w-5 h-5" />
                <span>Unlock the Full Guide</span>
              </motion.button>
              
              <motion.button
                data-testid="hero-preview-button"
                onClick={onScrollToGate}
                className="btn-secondary flex items-center gap-2 text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>See What's Inside</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </>
          )}
        </motion.div>
        
        {/* Access Status Indicator */}
        {isUnlocked && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-12 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-sm"
          >
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Full Guide Unlocked
          </motion.div>
        )}
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-2 bg-cyan-400 rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
}
